import { createLayer } from '../factory';
import { GALLERY_SRC_PREFIX } from '../gallery/illustrations';
import { PHOTOS, type PhotoId } from '../gallery/photos';
import type { LocaleId } from '../locales';
import { sceneTimeline } from '../motion/compile';
import type {
  CameraStep,
  DocMotion,
  EmphasisStep,
  LayerMotion,
  MotionFx,
  MotionKey,
  MotionScene,
  MotionSplit,
  MotionStep,
} from '../motion/schema';
import type { Fill, ImageLayer, Layer, ShapeLayer, TemplateDoc, TextLayer } from '../template';
import { isVerticalVideo, VERTICAL_TEMPLATE_MARGINS } from '../video-formats';

/**
 * Building blocks of the video gallery: a builder that lays a template out for any canvas
 * (safe area, unit sizes), keeps layer names unique, places scenes on the timeline and turns
 * scene-relative timings (key frames, camera moves, effects) into video time.
 */

export type Tx = Record<LocaleId, string>;

/** Inline translation, in the order used by the banner gallery: en, uk, ru, pl, ro, es. */
export const t = (en: string, uk: string, ru: string, pl: string, ro: string, es: string): Tx => ({
  en,
  uk,
  ru,
  pl,
  ro,
  es,
});

const NBSP = ' ';

function group(n: number, sep: string): string {
  const [int, frac] = String(n).split('.');
  const out = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return frac ? `${out}${sep === ',' ? '.' : ','}${frac}` : out;
}

/** Amounts per currency: USD (en), UAH (uk), RUB (ru), PLN (pl), RON (ro), EUR (es). */
export interface Amounts {
  usd: number;
  uah: number;
  rub: number;
  pln: number;
  ron: number;
  eur: number;
}

/** A price in the currency of the language, written the local way. */
export function money(a: Amounts): Tx {
  return {
    en: `$${group(a.usd, ',')}`,
    uk: `${group(a.uah, NBSP)}${NBSP}₴`,
    ru: `${group(a.rub, NBSP)}${NBSP}₽`,
    pl: `${group(a.pln, NBSP)}${NBSP}zł`,
    ro: `${group(a.ron, '.')}${NBSP}lei`,
    es: `${group(a.eur, '.')}${NBSP}€`,
  };
}

/** Joins translations piece by piece: tj(money(...), t('/mo', ...)). */
export function tj(...parts: Array<Tx | string>): Tx {
  const out = {} as Tx;
  for (const l of ['en', 'uk', 'ru', 'pl', 'ro', 'es'] as LocaleId[])
    out[l] = parts.map((p) => (typeof p === 'string' ? p : p[l])).join('');
  return out;
}

export const solid = (color: string): Fill => ({ type: 'solid', color });
export const lin = (angle: number, ...colors: string[]): Fill => ({
  type: 'linear',
  angle,
  stops: colors.map((color, i) => ({ offset: i / (colors.length - 1), color })),
});
/** Linear fill with explicit stops: [[offset, color], …]. */
export const linStops = (angle: number, stops: Array<[number, string]>): Fill => ({
  type: 'linear',
  angle,
  stops: stops.map(([offset, color]) => ({ offset, color })),
});
export const rad = (...colors: string[]): Fill => ({
  type: 'radial',
  stops: colors.map((color, i) => ({ offset: i / (colors.length - 1), color })),
});

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Mode = 'tall' | 'portrait' | 'square' | 'wide';

export interface TransitionSpec {
  preset: string;
  duration?: number;
  easing?: string;
  params?: Record<string, string | number | boolean>;
}

export interface SceneSpec {
  key: string;
  name: Tx;
  duration: number;
  /** Into the next scene; ignored on the last one. */
  transition?: TransitionSpec | null;
}

export interface SceneRef {
  id: string;
  key: string;
  /** Video time, ms. */
  start: number;
  end: number;
  duration: number;
}

/** A key frame at `at` ms after the scene starts (negative: before it ends). */
export type RelKey = Omit<MotionKey, 't'> & { at: number };

export interface MotionOpts {
  enter?: MotionStep | null;
  exit?: MotionStep | null;
  emphasis?: Partial<EmphasisStep> & Pick<EmphasisStep, 'preset' | 'duration'>;
  split?: Partial<MotionSplit>;
  keys?: RelKey[];
  /** Text effect drawn with the layer's text and font (text layers). */
  effect?: LayerMotion['effect'];
}

/** Enter/exit step: preset, delay after the scene start, duration. */
export const step = (
  preset: string,
  delay = 0,
  duration = 600,
  easing?: string,
  params?: Record<string, string | number | boolean>,
): MotionStep => ({
  preset,
  delay,
  duration,
  ...(easing ? { easing } : {}),
  ...(params ? { params } : {}),
});

/** Emphasis step: plays `repeat` times (0 = until the scene ends) after `delay`. */
export const emph = (
  preset: string,
  delay: number,
  duration: number,
  repeat = 1,
  gap = 0,
  params?: Record<string, string | number | boolean>,
): MotionOpts['emphasis'] => ({
  preset,
  delay,
  duration,
  repeat,
  gap,
  ...(params ? { params } : {}),
});

export interface BuildOpts {
  width: number;
  height: number;
  locale: LocaleId;
}

/** Number of pieces a text animator moves (an estimate for lines). */
export function pieceCount(text: string, by: MotionSplit['by']): number {
  const words = text.split(/\s+/).filter(Boolean);
  if (by === 'word') return words.length;
  if (by === 'char') return words.reduce((a, w) => a + [...w].length, 0);
  if (by === 'line') return Math.max(1, text.split('\n').length) + 2;
  return 1;
}

export class VideoBuilder {
  readonly W: number;
  readonly H: number;
  /** min(width, height): every size is a share of it. */
  readonly u: number;
  readonly mode: Mode;
  readonly locale: LocaleId;
  /** Where text and key elements go: centred frame sized like the apps' safe area on vertical videos, margins otherwise. */
  readonly F: Box;
  readonly scenes: SceneRef[] = [];
  readonly layers: Layer[] = [];
  readonly fx: MotionFx[] = [];
  readonly camera: CameraStep[] = [];
  private readonly sceneSpecs: MotionScene[] = [];
  private readonly names = new Set<string>();
  private readonly byKey = new Map<string, SceneRef>();
  private seq = 0;

  constructor(opts: BuildOpts, specs: SceneSpec[]) {
    this.W = opts.width;
    this.H = opts.height;
    this.u = Math.min(this.W, this.H);
    this.locale = opts.locale;
    const r = this.H / this.W;
    this.mode = isVerticalVideo(this.W, this.H)
      ? 'tall'
      : r > 1.12
        ? 'portrait'
        : r < 1 / 1.25
          ? 'wide'
          : 'square';
    const z =
      this.mode === 'tall'
        ? VERTICAL_TEMPLATE_MARGINS
        : { top: 0.06, bottom: 0.06, left: 0.06, right: 0.06 };
    this.F = {
      x: Math.ceil(z.left * this.W),
      y: Math.ceil(z.top * this.H),
      w: Math.floor(this.W * (1 - z.left - z.right)),
      h: Math.floor(this.H * (1 - z.top - z.bottom)),
    };
    specs.forEach((s, i) => {
      const last = i === specs.length - 1;
      this.sceneSpecs.push({
        id: `s${i + 1}-${s.key}`,
        name: s.name[this.locale] ?? s.name.en,
        duration: s.duration,
        transition:
          !last && s.transition
            ? {
                preset: s.transition.preset,
                duration: s.transition.duration ?? 600,
                ...(s.transition.easing ? { easing: s.transition.easing } : {}),
                ...(s.transition.params ? { params: s.transition.params } : {}),
              }
            : null,
      });
    });
    const timeline = sceneTimeline(this.motionBase());
    timeline.scenes.forEach((p, i) => {
      const ref: SceneRef = {
        id: p.id,
        key: specs[i].key,
        start: p.start,
        end: p.end,
        duration: p.end - p.start,
      };
      this.scenes.push(ref);
      this.byKey.set(ref.key, ref);
    });
  }

  get wide() {
    return this.mode === 'wide';
  }
  get tall() {
    return this.mode === 'tall';
  }
  /** Roomy vertical layouts: vertical and 4:5. */
  get upright() {
    return this.mode === 'tall' || this.mode === 'portrait';
  }
  get duration() {
    return this.scenes[this.scenes.length - 1].end;
  }

  sc(key: string): SceneRef {
    const s = this.byKey.get(key);
    if (!s) throw new Error(`Unknown scene ${key}`);
    return s;
  }

  tx(v: Tx | string): string {
    return typeof v === 'string' ? v : (v[this.locale] ?? v.en);
  }

  /** Size: share of min(width, height), rounded. */
  s(k: number): number {
    return Math.round(this.u * k);
  }

  /** Value by layout mode: pick({ tall, portrait, square, wide }), missing ones fall back. */
  pick<V>(v: { tall?: V; portrait?: V; square?: V; wide?: V; upright?: V; other?: V }): V {
    const m = this.mode;
    const direct = v[m];
    if (direct !== undefined) return direct;
    if ((m === 'tall' || m === 'portrait') && v.upright !== undefined) return v.upright;
    if (m === 'tall' && v.portrait !== undefined) return v.portrait;
    if (m === 'portrait' && v.tall !== undefined) return v.tall;
    if (v.other !== undefined) return v.other;
    return (v.square ?? v.portrait ?? v.tall ?? v.wide ?? v.upright) as V;
  }

  /** Box of the safe frame by shares: fx, fy, fw, fh in 0..1. */
  in(fx: number, fy: number, fw: number, fh: number): Box {
    const F = this.F;
    return { x: F.x + F.w * fx, y: F.y + F.h * fy, w: F.w * fw, h: F.h * fh };
  }

  full(): Box {
    return { x: 0, y: 0, w: this.W, h: this.H };
  }

  /** A box of size w×h centred on (cx, cy). */
  centered(cx: number, cy: number, w: number, h: number): Box {
    return { x: cx - w / 2, y: cy - h / 2, w, h };
  }

  /** Horizontal centre of the safe frame. */
  get cx() {
    return this.F.x + this.F.w / 2;
  }
  get cy() {
    return this.F.y + this.F.h / 2;
  }

  private name(n: string): string {
    let out = n;
    for (let i = 2; this.names.has(out); i++) out = `${n}_${i}`;
    this.names.add(out);
    return out;
  }

  private motion(scene: SceneRef | null, m: MotionOpts = {}): LayerMotion {
    const out: LayerMotion = {};
    if (scene) out.scene = scene.id;
    if (m.enter) out.enter = m.enter;
    if (m.exit) out.exit = m.exit;
    if (m.emphasis) out.emphasis = { delay: 0, repeat: 1, gap: 0, ...m.emphasis } as EmphasisStep;
    if (m.split) out.split = { by: 'none', stagger: 40, order: 'start', ...m.split };
    if (m.effect) out.effect = m.effect;
    if (m.keys?.length) {
      const from = scene?.start ?? 0;
      const to = scene?.end ?? this.duration;
      out.keys = m.keys
        .map(({ at, ...k }) => ({
          ...k,
          t: Math.round(Math.max(from, Math.min(to, at < 0 ? to + at : from + at))),
        }))
        .sort((a, b) => a.t - b.t);
    }
    return out;
  }

  private place<L extends Layer>(layer: L, box: Box): L {
    layer.x = Math.round(box.x);
    layer.y = Math.round(box.y);
    layer.width = Math.max(1, Math.round(box.w));
    layer.height = Math.max(1, Math.round(box.h));
    return layer;
  }

  private push<L extends Layer>(layer: L): L {
    layer.id = `vg${++this.seq}_${layer.name}`.slice(0, 64);
    this.layers.push(layer);
    return layer;
  }

  text(
    scene: SceneRef | null,
    name: string,
    value: Tx | string,
    box: Box,
    style: Partial<TextLayer> = {},
    m?: MotionOpts,
  ): TextLayer {
    const layer = createLayer('text', {
      name: this.name(name),
      text: this.tx(value),
      fontFamily: 'Inter',
      fontWeight: 700,
      fontSize: this.s(0.05),
      color: '#FFFFFF',
      align: 'center',
      verticalAlign: 'middle',
      lineHeight: 1.1,
      autoFit: true,
      minFontSize: Math.max(10, this.s(0.016)),
      ...style,
      motion: this.motion(scene, m),
    } as Partial<Layer>) as TextLayer;
    return this.push(this.place(layer, box));
  }

  shape(
    scene: SceneRef | null,
    name: string,
    box: Box,
    props: Partial<ShapeLayer> = {},
    m?: MotionOpts,
  ): ShapeLayer {
    const layer = createLayer('shape', {
      name: this.name(name),
      shape: 'rectangle',
      fill: solid('#FFFFFF'),
      ...props,
      motion: this.motion(scene, m),
    } as Partial<Layer>) as ShapeLayer;
    return this.push(this.place(layer, box));
  }

  /** Rough width of one line of text (k: average glyph width / font size). */
  textW(value: Tx | string, size: number, k = 0.6): number {
    return [...this.tx(value)].length * size * k;
  }

  /**
   * A pill with a label (one text layer with a background), as wide as its text needs. `at` is
   * the anchor: x of the left edge, centre or right edge (`align`) and y of the top.
   */
  chip(
    scene: SceneRef | null,
    name: string,
    value: Tx | string,
    at: { x: number; y: number; h: number; align?: 'left' | 'center' | 'right'; maxW?: number },
    style: {
      bg: Fill | string;
      color: string;
      font?: string;
      weight?: number;
      size?: number;
      upper?: boolean;
      radius?: number;
      k?: number;
      extra?: Partial<TextLayer>;
    },
    m?: MotionOpts,
  ): TextLayer {
    const size = style.size ?? Math.round(at.h * 0.46);
    const padX = Math.round(at.h * 0.42);
    const k = style.k ?? (style.upper ? 0.7 : 0.6);
    const align = at.align ?? 'left';
    // Never wider than the room left inside the safe frame on the anchored side(s)
    const F = this.F;
    const room =
      align === 'left'
        ? F.x + F.w - at.x
        : align === 'right'
          ? at.x - F.x
          : 2 * Math.min(at.x - F.x, F.x + F.w - at.x);
    const maxW = Math.max(at.h, Math.min(at.maxW ?? F.w, Math.floor(room)));
    const w = Math.min(maxW, Math.round(this.textW(value, size, k) + padX * 2));
    const x = align === 'left' ? at.x : align === 'center' ? at.x - w / 2 : at.x - w;
    return this.text(
      scene,
      name,
      value,
      { x, y: at.y, w, h: at.h },
      {
        fontFamily: style.font ?? 'Inter',
        fontWeight: style.weight ?? 700,
        fontSize: size,
        color: style.color,
        background: typeof style.bg === 'string' ? solid(style.bg) : style.bg,
        borderRadius: style.radius ?? Math.round(at.h / 2),
        padding: { top: 0, bottom: 0, left: padX, right: padX },
        textTransform: style.upper ? 'uppercase' : 'none',
        letterSpacing: style.upper ? Math.round(size * 0.06) : 0,
        maxLines: 1,
        ...style.extra,
      },
      m,
    );
  }

  icon(
    scene: SceneRef | null,
    name: string,
    icon: string,
    box: Box,
    color: string,
    m?: MotionOpts,
  ): ShapeLayer {
    return this.shape(scene, name, box, { shape: 'icon', icon, fill: solid(color) }, m);
  }

  photo(
    scene: SceneRef | null,
    name: string,
    photo: PhotoId,
    box: Box,
    props: Partial<ImageLayer> = {},
    m?: MotionOpts,
  ): ImageLayer {
    const p = PHOTOS[photo] as { focusX?: number; focusY?: number };
    const layer = createLayer('image', {
      name: this.name(name),
      src: `${GALLERY_SRC_PREFIX}${photo}`,
      fit: 'cover',
      focusX: p.focusX ?? 0.5,
      focusY: p.focusY ?? 0.5,
      ...props,
      motion: this.motion(scene, m),
    } as Partial<Layer>) as ImageLayer;
    return this.push(this.place(layer, box));
  }

  /** An image from any source: an uploaded file, a brand logo reference, a gallery photo. */
  image(
    scene: SceneRef | null,
    name: string,
    src: string,
    box: Box,
    props: Partial<ImageLayer> = {},
    m?: MotionOpts,
  ): ImageLayer {
    const id = src.startsWith(GALLERY_SRC_PREFIX) ? src.slice(GALLERY_SRC_PREFIX.length) : '';
    const p = (PHOTOS as Record<string, { focusX?: number; focusY?: number }>)[id];
    const layer = createLayer('image', {
      name: this.name(name),
      src,
      fit: 'cover',
      focusX: p?.focusX ?? 0.5,
      focusY: p?.focusY ?? 0.5,
      ...props,
      motion: this.motion(scene, m),
    } as Partial<Layer>) as ImageLayer;
    return this.push(this.place(layer, box));
  }

  /** Full-canvas colour block of a scene (its background). */
  backdrop(scene: SceneRef, name: string, fill: Fill, m?: MotionOpts): ShapeLayer {
    return this.shape(scene, name, this.full(), { fill }, m);
  }

  /** Gradient over a photo so text on it stays readable. */
  shade(
    scene: SceneRef,
    name: string,
    box: Box,
    stops: Array<[number, string]>,
    angle = 180,
    m?: MotionOpts,
  ): ShapeLayer {
    return this.shape(scene, name, box, { fill: linStops(angle, stops) }, m);
  }

  /** Slow zoom (and drift) through the whole scene: the Ken Burns move as key frames. */
  kenBurns(from = 1.04, to = 1.16, dx = 0, dy = 0): RelKey[] {
    return [
      { at: 0, scale: from, easing: 'linear' },
      { at: -1, scale: to, x: Math.round(dx * this.u), y: Math.round(dy * this.u) },
    ];
  }

  /** A hard punch-in on the cut that settles into a slow push. */
  punchIn(peak = 1.22, settle = 1, drift = 1.06, hit = 260): RelKey[] {
    return [
      { at: 0, scale: peak, easing: 'out-expo' },
      { at: hit, scale: settle, easing: 'linear' },
      { at: -1, scale: drift },
    ];
  }

  addFx(
    scene: SceneRef | null,
    kind: string,
    at: number,
    duration: number,
    params: Record<string, string | number | boolean> = {},
    z?: 'front' | 'back',
  ): void {
    const from = scene?.start ?? 0;
    const to = scene?.end ?? this.duration;
    const start = Math.max(from, Math.min(to - 16, from + at));
    this.fx.push({
      id: `fx-${this.fx.length + 1}-${kind}`,
      kind,
      start: Math.round(start),
      duration: Math.round(Math.max(16, Math.min(duration, to - start))),
      z: z ?? 'front',
      params,
    });
  }

  addCamera(
    scene: SceneRef,
    preset: string,
    at: number,
    duration: number,
    params: Record<string, string | number | boolean> = {},
  ): void {
    const start = Math.max(scene.start, Math.min(scene.end - 16, scene.start + at));
    this.camera.push({
      id: `cam-${this.camera.length + 1}-${preset}`,
      preset,
      start: Math.round(start),
      duration: Math.round(Math.max(16, Math.min(duration, scene.end - start))),
      params,
    });
  }

  private motionBase(): DocMotion {
    return {
      duration: this.sceneSpecs.reduce((a, s) => a + s.duration, 0),
      fps: 30,
      opener: null,
      scenes: this.sceneSpecs,
      fx: [],
      camera: [],
      loops: 1,
      tracks: [],
      duck: 0.3,
    };
  }

  /** Keeps every step inside its scene (long translations make text animators longer). */
  private fit(): void {
    const byId = new Map(this.scenes.map((s) => [s.id, s]));
    for (const l of this.layers) {
      const m = l.motion;
      const scene = m?.scene ? byId.get(m.scene) : undefined;
      if (!m || !scene) continue;
      const D = scene.duration;
      const pieces =
        m.split && m.split.by !== 'none' && l.type === 'text' ? pieceCount(l.text, m.split.by) : 1;
      if (m.enter) {
        m.enter.duration = Math.min(m.enter.duration, Math.round(D * 0.7));
        let spread = m.split ? m.split.stagger * (pieces - 1) : 0;
        const room = D - 120 - m.enter.duration;
        if (m.enter.delay + spread > room && m.split) {
          m.split.stagger = Math.max(
            0,
            Math.floor(Math.max(0, room - m.enter.delay) / Math.max(1, pieces - 1)),
          );
          spread = m.split.stagger * (pieces - 1);
        }
        m.enter.delay = Math.max(0, Math.min(m.enter.delay, room - spread));
      }
      if (m.exit) {
        m.exit.duration = Math.min(m.exit.duration, Math.round(D * 0.4));
        m.exit.delay = Math.max(0, Math.min(m.exit.delay, D - m.exit.duration));
      }
      if (m.emphasis) {
        const e = m.emphasis;
        e.delay = Math.min(e.delay, Math.max(0, D - e.duration));
        if (e.delay + e.duration > D) e.duration = Math.max(16, D - e.delay);
        if (e.repeat > 0) {
          const fits = Math.floor((D - e.delay + e.gap) / (e.duration + e.gap));
          e.repeat = Math.max(1, Math.min(e.repeat, fits));
        }
      }
    }
  }

  /** Scene shown as the still picture of the video. */
  poster?: string;

  build(background: Fill | null): TemplateDoc {
    this.fit();
    const poster = this.poster ? this.byKey.get(this.poster)?.id : undefined;
    return {
      width: this.W,
      height: this.H,
      background,
      layers: this.layers,
      motion: {
        ...this.motionBase(),
        fx: this.fx,
        camera: this.camera,
        ...(poster ? { poster } : {}),
      },
    };
  }
}
